import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useApolloClient, useMutation } from '@apollo/client/react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@scio3/ui';
import {
  DELETE_COMPANY,
  REACTIVATE_WITH_TRIAL,
  ME_QUERY,
  GET_COMPANY_INFO,
  type MeQueryResult,
  type GetCompanyInfoQueryResult,
  type ReactivateWithTrialResult,
  type ReactivateWithTrialVariables,
} from '@scio3/graphql-client';
import { signOut, auth } from '@/lib/firebase';
import { analyticsReactivated } from '@/lib/analytics-service';
import { getStoredWinbackUtm, clearWinbackUtm } from '@/lib/winback-utm';
import { useAuthStore } from '@/stores/auth-store';
import { usePermissions } from '@/hooks/usePermissions';
import { useDialogSubmitState } from '@/hooks/useDialogSubmitState';
import { useBillingDialogStore } from '@/features/billing/stores/billing-dialog-store';
import { useBillingData } from '@/features/billing/hooks/useBillingData';
import { SubscriptionWizardDialog } from '@/features/billing/dialogs/SubscriptionWizardDialog';
import { BillingInformationDialog } from '@/features/billing/dialogs/BillingInformationDialog';
import { DowngradeDialog } from '@/features/billing/dialogs/DowngradeDialog';
import { DowngradeComparisonDialog } from '@/features/billing/dialogs/DowngradeComparisonDialog';
import { DowngradeSurveyDialog } from '@/features/billing/dialogs/DowngradeSurveyDialog';
import { UnpaidInvoicesDialog } from '@/features/billing/dialogs/UnpaidInvoicesDialog';
import { ScreenManagementDialogFromStore } from '@/features/billing/dialogs/BillingDialogs';

interface PaymentData {
  isTrial?: boolean;
  isActive?: boolean;
}

/**
 * Reason-specific copy for a rejected winback claim. The server returns the
 * cause as `WINBACK_*` in `message` (house Group-1 style: an in-band rejection,
 * not a thrown GraphQL error), and `handleClaimTrial` rethrows it as an Error —
 * so the code lands in `err.message`. A blanket "please try again" is wrong for
 * a terminal rejection (already claimed / cap reached) and hides the cause from
 * both the user and support.
 */
function winbackClaimError(code: string, t: (key: string) => string): string {
  switch (code) {
    case 'WINBACK_ALREADY_CLAIMED':
      return t('You have already claimed this free trial offer.');
    case 'WINBACK_LIMIT_REACHED':
    case 'WINBACK_NOT_ELIGIBLE':
    case 'WINBACK_FORCE_INACTIVE':
      return t('This free trial offer is not available for your account.');
    default:
      // Transport / unexpected GraphQL failure — retrying is genuinely useful.
      return t('Failed to claim your free trial. Please try again or contact support.');
  }
}

export function isOptiSignsDomain(): boolean {
  const host = window.location.hostname;
  return (
    host === 'localhost' ||
    host === 'optisigns.com' ||
    host.endsWith('.optisigns.com') ||
    host.endsWith('.opstech.org')
  );
}

// ---------------------------------------------------------------------------
// DeleteAccountDialog — separate confirmation dialog matching scio1 UX
// ---------------------------------------------------------------------------

function DeleteAccountDialog({
  open,
  onOpenChange,
  onConfirm,
  deleting,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  deleting: boolean;
}) {
  const { t } = useTranslation();
  const [confirmText, setConfirmText] = useState('');
  const isConfirmed = confirmText === 'DELETE';
  const { disabled, disabledTooltip } = useDialogSubmitState({
    isSubmitting: deleting,
    isConfirmed,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="delete-account-dialog">
        <DialogHeader>
          <DialogTitle>{t('Delete Account')}</DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-4">
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                {t('deleteAccountModal1_prefix')}{' '}
                <span className="font-semibold text-destructive">
                  {t('This action cannot be undone.')}
                </span>{' '}
                {t('Do you wish to continue?')}
              </div>
              <div className="space-y-2">
                <Input
                  data-testid="delete-account-confirm-input"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="DELETE"
                />
                <p className="text-sm text-muted-foreground">
                  {t('Please type "DELETE" and click Delete Account to continue.')}
                </p>
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">{t('Cancel')}</Button>
          </DialogClose>
          <Button
            variant="destructive"
            data-testid="delete-account-ok-btn"
            onClick={onConfirm}
            disabled={disabled}
            disabledTooltip={disabledTooltip}
          >
            {t('OK')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// InactiveAccountDialog — main blocking dialog
// ---------------------------------------------------------------------------

export function InactiveAccountDialog() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const me = useAuthStore((s) => s.me);
  const setMe = useAuthStore((s) => s.setMe);
  const setCompany = useAuthStore((s) => s.setCompany);
  const { isRootOwner } = usePermissions();
  const [deleteCompany] = useMutation(DELETE_COMPANY);
  const [reactivateWithTrial] = useMutation<
    ReactivateWithTrialResult,
    ReactivateWithTrialVariables
  >(REACTIVATE_WITH_TRIAL);
  const [deleting, setDeleting] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const openBillingDialog = useBillingDialogStore((s) => s.openDialog);
  const activeBillingDialog = useBillingDialogStore((s) => s.activeDialog);
  // Reads from the auth store (instant, no network) — gives the account's
  // remaining screen quota, used to tell "cancelled" apart from "card declined".
  const { maxScreens } = useBillingData();

  // Hide the Attention alert while a child dialog (Delete Account confirm or any billing dialog)
  // is open so it doesn't visually stack on top. When the child closes and the account is still
  // inactive, AppShell keeps rendering us → the alert reappears.
  const childDialogOpen = showDeleteDialog || activeBillingDialog !== null;

  const payment = me?.payment as PaymentData | undefined;
  const isTrial = payment?.isTrial === true;
  const isWhiteLabel = !!me?.brandingData?.isWhiteLabel;
  const showUpdate = isOptiSignsDomain() && !isWhiteLabel;
  const showDelete = isRootOwner && !isWhiteLabel;
  // Winback (OptiReach) clicker whose session survived — the login-page claim
  // never ran, so offer the 30-day trial from the paywall gate. The capture was
  // persisted to localStorage by AppShell's auth-page capture (or an earlier
  // /signIn visit); OptiSigns-branded offer, so hidden on white-label (same
  // gate as Update).
  const winbackUtm = getStoredWinbackUtm();
  const showClaimTrial = !!winbackUtm && !isWhiteLabel;

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch {
      // Even if signOut fails, navigate away — auth state clears on next load
    }
    navigate('/signIn');
  };

  // Open the native scio3 billing flow. Recovery: the wizard/BillingInformationDialog
  // refetches ME_QUERY on success → auth-store `isAccountInactive` flips false →
  // AppShell unmounts this paywall gate automatically.
  //
  // #1413 — Route to the right flow based on WHY the account is inactive:
  //  - Trial expired OR subscription cancelled (no remaining screen quota,
  //    maxScreens === 0): the user has no active subscription, so updating only
  //    the card would leave them at 0/0 screens. Open the subscription wizard,
  //    which starts at the "Number of Screens" step (defaulting to 1) so they can
  //    actually re-subscribe.
  //  - Payment failed but a subscription/quota still exists (maxScreens > 0):
  //    they only need a fresh card → keep the card-only Billing Information dialog.
  const needsResubscribe = isTrial || maxScreens === 0;
  const handleUpdate = () => {
    openBillingDialog(
      needsResubscribe ? 'subscriptionWizard' : 'billingInformation',
      needsResubscribe ? { fromInactive: true } : undefined,
    );
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteCompany();
      await signOut(auth);
      navigate('/signIn');
    } catch {
      toast.error(t('Failed to delete account. Please try again or contact support.'));
    } finally {
      setDeleting(false);
    }
  };

  // Claim the winback 30-day trial, then follow the recovery contract above:
  // refetch ME_QUERY + GET_COMPANY_INFO network-only and push them into the
  // auth-store → `isAccountInactive` flips false → AppShell unmounts this
  // paywall gate automatically (the PromotionalBanner countdown then confirms
  // the trial visually).
  const handleClaimTrial = async () => {
    if (!winbackUtm) return;
    setClaiming(true);
    try {
      const { data } = await reactivateWithTrial({ variables: { utmData: winbackUtm } });
      // A definitive server answer — granted OR rejected — spends the stored
      // capture (same rule as LoginPage `claimWinbackTrial`). Without this the
      // offer button would linger for the rest of the localStorage TTL window
      // and fail again on every paywall render. Transport failures throw before
      // reaching here, so the capture survives for a retry.
      clearWinbackUtm();
      if (!data?.reactivateWithTrial?.status) {
        throw new Error(data?.reactivateWithTrial?.message ?? 'reactivateWithTrial failed');
      }
      analyticsReactivated({ campaign: winbackUtm.utmCampaign ?? '' });
      const [meResult, companyResult] = await Promise.all([
        apolloClient.query<MeQueryResult>({ query: ME_QUERY, fetchPolicy: 'network-only' }),
        apolloClient.query<GetCompanyInfoQueryResult>({
          query: GET_COMPANY_INFO,
          fetchPolicy: 'network-only',
        }),
      ]);
      if (meResult.data?.me) setMe(meResult.data.me);
      if (companyResult.data?.getCompanyInfo) setCompany(companyResult.data.getCompanyInfo);
    } catch (err: unknown) {
      // Not eligible / already claimed / capped — the paywall stays; the user
      // can still use Update / Logout. Surface WHY (and log the raw code) so a
      // terminal rejection doesn't read as a transient glitch.
      const code = err instanceof Error ? err.message : '';
      console.warn('[winback] paywall claim failed:', code || err);
      toast.error(winbackClaimError(code, t));
    } finally {
      setClaiming(false);
    }
  };

  return (
    <>
      <AlertDialog open={!childDialogOpen}>
        <AlertDialogContent
          data-testid="inactive-account-dialog"
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>{t('Attention')}</AlertDialogTitle>
            <AlertDialogDescription>
              {isTrial
                ? t('freePlanDowngradeInactive')
                : t(
                    'Your subscription needs attention. Please update your payment information to continue.',
                  )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {/* flex-wrap + gap: with the winback claim button present the row is
              Logout + Update Subscription + Claim your 30-day free trial, which
              overflows AlertDialogContent's max-w-lg and gets clipped (the base
              footer is nowrap and Buttons are whitespace-nowrap). Wrapping also
              needs space-x-0 — the base `sm:space-x-2` would left-margin the
              wrapped line. */}
          <AlertDialogFooter className="flex-wrap gap-2 sm:flex-row sm:justify-between sm:space-x-0">
            {showDelete && (
              <Button
                variant="destructive"
                data-testid="inactive-delete-account-btn"
                onClick={() => setShowDeleteDialog(true)}
              >
                {t('Delete Account')}
              </Button>
            )}
            <div className="flex flex-wrap justify-end gap-2 sm:ml-auto">
              <Button variant="outline" data-testid="inactive-logout-btn" onClick={handleLogout}>
                {t('Logout')}
              </Button>
              {showUpdate && (
                <Button
                  variant={showClaimTrial ? 'outline' : 'default'}
                  data-testid="inactive-update-btn"
                  onClick={handleUpdate}
                >
                  {isTrial ? t('Update Subscription') : t('Update')}
                </Button>
              )}
              {showClaimTrial && (
                <Button
                  data-testid="inactive-claim-trial-btn"
                  onClick={handleClaimTrial}
                  disabled={claiming}
                >
                  {claiming ? t('Claiming...') : t('Claim your 30-day free trial')}
                </Button>
              )}
            </div>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {showDelete && (
        <DeleteAccountDialog
          open={showDeleteDialog}
          onOpenChange={setShowDeleteDialog}
          onConfirm={handleDelete}
          deleting={deleting}
        />
      )}

      {/* Mount native billing dialogs here so they're available during the paywall lockout —
          AppShell's isAccountInactive branch returns early, before <BillingDialogs /> mounts.
          SubscriptionWizardDialog → Free selection opens DowngradeDialog and can surface
          UnpaidInvoicesDialog as a pre-check, so both must be mounted alongside.
          ScreenManagementDialogFromStore is also needed because the wizard's trial→Free
          5555 path calls openScreenManagement(...) on the global store — without this
          mount the store update has no listener and the dialog never appears. */}
      <SubscriptionWizardDialog />
      <BillingInformationDialog />
      <DowngradeSurveyDialog />
      <DowngradeComparisonDialog />
      <DowngradeDialog />
      <UnpaidInvoicesDialog />
      <ScreenManagementDialogFromStore />
    </>
  );
}
